import { useAtomValue } from "@effect/atom-react";
import { EnvironmentId, type ResolvedKeybindingsConfig } from "@afar-research/contracts";
import * as Option from "effect/Option";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from "react";

import { resolveShortcutCommand, shortcutLabelForCommand } from "../keybindings";
import { readLocalApi } from "../localApi";
import { primaryServerKeybindingsAtom } from "../state/server";
import { useWorkspaceShellEntry } from "../state/workspaceShellEntry";
import {
  createWorkspaceShellPlatformAdapter,
  resolveSupportedClientSurface,
  type SupportedClientSurface,
} from "../workspaceShellPlatform";
import ThreadSidebar from "./Sidebar";
import { Sidebar, SidebarProvider, SidebarRail, SidebarTrigger, useSidebar } from "./ui/sidebar";
import { Tooltip, TooltipPopup, TooltipTrigger } from "./ui/tooltip";
import { openFeedbackDialog } from "./settings/FeedbackDialog";
import { isClientLocalSettingsPath } from "./settings/feedbackSettings.logic";
import { WorkspaceShellEntryState } from "./WorkspaceShellEntryState";
import { useRouteEnvironmentId } from "../hooks/useRouteEnvironmentId";
import {
  cloudWorkspaceIdsAtom,
  sessionCloudWorkspaceIdAtom,
} from "../connection/cloudWorkspaceRegistration";
import { environmentShell } from "../state/shell";
import { useEnvironment } from "../state/environments";
import {
  WorkspaceEmptyStateFrame,
  WorkspaceEmptyStateInset,
  WorkspaceEmptyStateMessage,
} from "./WorkspaceEmptyStateFrame";
import { Button } from "./ui/button";
import { resolveCloudWorkspaceEntry } from "./CloudWorkspaceEntryGate.logic";

const THREAD_SIDEBAR_WIDTH_STORAGE_KEY = "chat_thread_sidebar_width";
const THREAD_SIDEBAR_MIN_WIDTH = 13 * 16;
const THREAD_MAIN_CONTENT_MIN_WIDTH = 40 * 16;

export { resolveSupportedClientSurface, type SupportedClientSurface };
export * from "./WorkspaceShellPrimitives";

function SidebarControl({ keybindings }: { readonly keybindings: ResolvedKeybindingsConfig }) {
  const { toggleSidebar } = useSidebar();
  const shortcutLabel = shortcutLabelForCommand(keybindings, "sidebar.toggle");

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;
      if (resolveShortcutCommand(event, keybindings) !== "sidebar.toggle") return;

      event.preventDefault();
      event.stopPropagation();
      toggleSidebar();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [keybindings, toggleSidebar]);

  return (
    <div
      className="pointer-events-none fixed left-[var(--workspace-controls-left)] top-[var(--workspace-controls-top)] z-50 flex h-[var(--workspace-topbar-height)] items-center"
      data-sidebar-control=""
    >
      <Tooltip>
        <TooltipTrigger
          render={
            <SidebarTrigger className="pointer-events-auto" aria-label="Toggle main sidebar" />
          }
        />
        <TooltipPopup side="bottom">
          Toggle main sidebar{shortcutLabel ? ` (${shortcutLabel})` : ""}
        </TooltipPopup>
      </Tooltip>
    </div>
  );
}

/**
 * Holds every page back until the shell can open, so nothing reachable from the workspace
 * acts on an Environment that has not delivered its first snapshot. Device-local settings
 * never reach an Environment and are where a connection is repaired, so they stay open.
 */
function WorkspaceShellEntryGate({ children }: { readonly children: ReactNode }) {
  const entry = useWorkspaceShellEntry();
  const selectedId = useRouteEnvironmentId();
  const cloudWorkspaceIds = useAtomValue(cloudWorkspaceIdsAtom);
  const sessionCloudId = useAtomValue(sessionCloudWorkspaceIdAtom);
  // A route that names no Environment still waits on the workspace this session keeps connected.
  const candidate = selectedId ?? sessionCloudId;
  const cloudId =
    candidate !== null && cloudWorkspaceIds.ids.has(candidate)
      ? EnvironmentId.make(candidate)
      : null;
  const clientLocal = useLocation({
    select: (location) =>
      isClientLocalSettingsPath(readLocalApi()?.connect !== undefined, location.pathname),
  });
  if (clientLocal) return children;
  if (cloudId !== null)
    return (
      <CloudWorkspaceEntryGate key={cloudId} environmentId={cloudId}>
        {children}
      </CloudWorkspaceEntryGate>
    );
  if (entry.kind === "ready") return children;
  return <WorkspaceShellEntryState entry={entry} />;
}

function CloudWorkspaceEntryGate(props: {
  readonly environmentId: EnvironmentId;
  readonly children: ReactNode;
}) {
  const [slow, setSlow] = useState(false);
  const [entered, setEntered] = useState(false);
  const shell = useAtomValue(environmentShell.stateValueAtom(props.environmentId));
  const environment = useEnvironment(props.environmentId);
  const snapshot = Option.isSome(shell.snapshot);
  const connected = snapshot && shell.status === "ready";
  const booting = environment?.connection.endpointStarting ?? false;
  const entry = resolveCloudWorkspaceEntry({
    entered,
    hasSnapshot: snapshot,
    shellReady: shell.status === "ready",
    booting,
    unavailable:
      shell.status === "unavailable" && environment?.connection.phase === "unavailable" && !booting,
  });
  const view = entry.view;
  useEffect(() => setEntered(entry.entered), [entry.entered]);
  // Each entry, including one after the machine boots again, gets its own patience.
  useEffect(() => {
    setSlow(false);
    if (entry.entered) return;
    const timer = window.setTimeout(() => setSlow(true), 60_000);
    return () => window.clearTimeout(timer);
  }, [entry.entered]);
  if (view === "unavailable")
    return (
      <WorkspaceShellEntryState
        entry={{ kind: "unavailable", environmentId: props.environmentId }}
      />
    );
  if (view === "workspace") return props.children;
  return (
    <WorkspaceEmptyStateInset>
      <WorkspaceEmptyStateFrame heading="Cloud Workspace">
        <CloudWorkspaceBootProgress
          snapshot={connected}
          booting={booting}
          shellStatus={shell.status}
          slow={slow}
          onRetry={() => window.location.reload()}
        />
      </WorkspaceEmptyStateFrame>
    </WorkspaceEmptyStateInset>
  );
}

export function CloudWorkspaceBootProgress(props: {
  readonly snapshot: boolean;
  readonly booting?: boolean;
  readonly shellStatus: string;
  readonly slow?: boolean;
  readonly onRetry: () => void;
}) {
  const starting = props.booting === true || (!props.snapshot && props.shellStatus === "starting");
  return (
    <WorkspaceEmptyStateMessage
      title={starting ? "Starting workspace" : props.snapshot ? "Ready" : "Connecting"}
      detail={
        <div role="status" className="space-y-3">
          <p>
            {props.slow
              ? "This is taking longer than usual. You can retry the connection."
              : starting
                ? "Your workspace is booting up. It opens once it is ready; this can take a minute."
                : "Your workspace will open when its Environment connects."}
          </p>
          <ol className="space-y-1 text-left text-xs">
            <li>1. Starting workspace {starting ? "…" : "✓"}</li>
            <li>2. Connecting {props.snapshot ? "✓" : "…"}</li>
          </ol>
        </div>
      }
      action={props.slow ? <Button onClick={props.onRetry}>Retry</Button> : undefined}
    />
  );
}

/**
 * The one persistent frame for every Supported Client Surface. Platform
 * adapters may add capabilities inside the workspace, but navigation,
 * responsive presentation, and sidebar state always enter through this seam.
 */
export function WorkspaceShell({ children }: { readonly children: ReactNode }) {
  const navigate = useNavigate();
  const keybindings = useAtomValue(primaryServerKeybindingsAtom);
  const platform = useMemo(createWorkspaceShellPlatformAdapter, []);
  const style = platform.controlsLeftInset
    ? ({ "--workspace-controls-left": platform.controlsLeftInset } as CSSProperties)
    : undefined;

  useEffect(() => {
    return platform.subscribeMenuAction("open-settings", () => void navigate({ to: "/settings" }));
  }, [navigate, platform]);
  useEffect(() => platform.subscribeMenuAction("send-feedback", openFeedbackDialog), [platform]);

  return (
    <WorkspaceShellFrame
      clientSurface={platform.clientSurface}
      keybindings={keybindings}
      navigation={<ThreadSidebar />}
      {...(style ? { style } : {})}
    >
      <WorkspaceShellEntryGate>{children}</WorkspaceShellEntryGate>
    </WorkspaceShellFrame>
  );
}

/**
 * Shared shell interface. The production adapter supplies live discovery;
 * focused tests can supply an in-memory navigation adapter at the same seam.
 */
export function WorkspaceShellFrame({
  children,
  clientSurface,
  keybindings,
  navigation,
  style,
}: {
  readonly children: ReactNode;
  readonly clientSurface: SupportedClientSurface;
  readonly keybindings: ResolvedKeybindingsConfig;
  readonly navigation: ReactNode;
  readonly style?: CSSProperties;
}) {
  return (
    <SidebarProvider
      className="h-dvh! min-h-0!"
      data-client-surface={clientSurface}
      data-workspace-shell=""
      defaultOpen
      style={style}
    >
      <Sidebar
        aria-label="Workspace navigation"
        role="navigation"
        side="left"
        collapsible="offcanvas"
        className="border-r border-sidebar-border bg-sidebar text-sidebar-foreground"
        resizable={{
          minWidth: THREAD_SIDEBAR_MIN_WIDTH,
          shouldAcceptWidth: ({ nextWidth, wrapper }) =>
            wrapper.clientWidth - nextWidth >= THREAD_MAIN_CONTENT_MIN_WIDTH,
          storageKey: THREAD_SIDEBAR_WIDTH_STORAGE_KEY,
        }}
      >
        {navigation}
        <SidebarRail aria-label="Resize workspace sidebar" />
      </Sidebar>
      {children}
      <SidebarControl keybindings={keybindings} />
    </SidebarProvider>
  );
}
